import {MemberAccount} from '../views/ec-members/account';
import {isDefined} from '../commons/commons';
import * as moment from 'moment-timezone';
import {ECMemberRelation, ECMemberRelationTargetType, ECMemberRelationType, ECRepresentativeAddress} from 'smag-commons/cfg/hierarchy';
import {AddressExt} from './google-maps-address-autocomplete/google-maps-address-autocomplete.component';
import {ECEconomicsThirdPartyProducerPod, ECPlantTipMode, ECPlantTipType} from 'smag-commons/dto/eco';
import {ECCacvRedistributionMode, ECEconomicsAdvancedUserPod} from 'smag-commons/dto/eco/community';
import {NiceValuePipe} from './shared.pipe';
import {I18NService} from '../i18n/i18n.service';
import {CACV_STRING} from './ec-economics';
import {AbstractControl, FormGroup, ValidationErrors} from '@angular/forms';
import {FormError, mkTextField} from './energy-community';
import {EC_DOCUMENT_TYPE_OTHER} from '../views/ec-members/new-member/member-form.service';
import {MsgBoxService} from './msg-box.service';
import * as _ from 'lodash';
import {EnergyCommunityNamedPlant} from 'smag-commons/cfg/hierarchy/ec';


export function mkRepresentativeAddress(address: AddressExt): ECRepresentativeAddress | null {
    if (!isDefined(address)) {
        return null;
    }
    const result = {
        street: address.street,
        street_number: address.streetNumber,
        city: address.municipality,
        postal_code: address.postalCode,
        province: address.province
    };
    // country is always defined, from region downwards it might not
    if (!isDefined(address.region)) {
        result.province = address.country;
    } else if (!isDefined(address.province)) {
        result.province = address.country;
        result.city = address.region;
    }
    return result;
}

export function isPasswordExpired(account: MemberAccount): boolean {
    if (!isDefined(account.pwExpiresOn)) {
        return false;
    }
    return moment().valueOf() > account.pwExpiresOn;
}

export function isMemberPodRelationActive(podRelation: ECMemberRelation): boolean {
    const now = moment().valueOf();
    return isDefined(podRelation.start_date) && podRelation.start_date <= now &&
        (!isDefined(podRelation.end_date) || podRelation.end_date > now);
}

export function isMemberHolder(relation: ECMemberRelation): boolean {
    return relation.type === ECMemberRelationType.HOLDER;
}

export function isMemberOwner(relation: ECMemberRelation): boolean {
    return relation.type === ECMemberRelationType.OWNER;
}

export function isRelationWithPod(relation: ECMemberRelation): boolean {
    return relation.target_type === ECMemberRelationTargetType.POD;
}

export function isRelationWithPropertyUnit(relation: ECMemberRelation): boolean {
    return relation.target_type === ECMemberRelationTargetType.PROPERTY_UNIT;
}

export function isRelationWithTargetPod(relation: ECMemberRelation, pod: string): boolean {
    return relation.target_id === pod && isRelationWithPod(relation);
}

export function isRelationWithTargetPropertyUnit(relation: ECMemberRelation, propertyUnitId: string): boolean {
    return relation.target_id === propertyUnitId && isRelationWithPropertyUnit(relation);
}

export function getMemberPodRelations(relations: ECMemberRelation[]) {
    return relations.filter(relation => isRelationWithPod(relation));
}

export function findMemberTargetPodRelation(relations: ECMemberRelation[], pod: string) {
    return relations.find(relation => isRelationWithTargetPod(relation, pod));
}

export function getMemberTargetPodRelations(relations: ECMemberRelation[], pod: string) {
    return relations.filter(relation => isRelationWithTargetPod(relation, pod));
}

export function getMemberTargetPropertyUnitRelations(relations: ECMemberRelation[], propertyUnitId: string) {
    return relations.filter(relation => isRelationWithTargetPropertyUnit(relation, propertyUnitId));
}

export function getHolderMemberRelations(relations: ECMemberRelation[]) {
    return relations.filter(relation => isMemberHolder(relation));
}

export function getOwnerMemberRelations(relations: ECMemberRelation[]) {
    return relations.filter(relation => isMemberOwner(relation));
}

export function getMemberRelationsForType(relations: ECMemberRelation[], type: ECMemberRelationType) {
    return relations.filter(relation => relation.type === type);
}

export function getHolderMemberWithTargetPodRelations(relations: ECMemberRelation[], pod: string) {
    return relations.filter(relation => isRelationWithTargetPod(relation, pod) && isMemberHolder(relation));
}

/**
 * It is assumed that the member is the POD's *current holder*.
 * The more recent holder relation for this pod is returned.
 */
export function isPodActive(relations: ECMemberRelation[], pod: string): boolean {
    const podRelations = sortMemberRelations(getHolderMemberWithTargetPodRelations(relations, pod));
    return podRelations.length ? isMemberPodRelationActive(relations[0]) : false;
}

export function isPlantActive(plant: EnergyCommunityNamedPlant): boolean {
    const hour = moment().valueOf();
    return isDefined(plant.plant.activation_date) && plant.plant.activation_date <= hour;
}


const BYTES_IN_MB = 1024 * 1000;
const BYTES_IN_KB = 1024;

export function prettySize(size: number) {
    return size > BYTES_IN_MB ? (size / BYTES_IN_MB).toFixed(1) + ' MB' : (size / BYTES_IN_KB).toFixed(1) + ' kB';
}


// cacv and tip for third party producer and advanced user
export interface NamedECEconomicsThirdPartyProducerPod extends ECEconomicsThirdPartyProducerPod {
    name: string;
    namedPlants: NamedECEconomicsThirdPartyProducerPlant[];
    isActive: boolean;
    generatesTip: boolean;
}

export interface NamedECEconomicsThirdPartyProducerPlant {
    name: string;
    isActive: boolean;
    generatesTip: boolean;
}

export interface NamedECEconomicsAdvancedUserPod extends ECEconomicsAdvancedUserPod {
    name: string;
    namedPlants: NamedECEconomicsAdvancedUserPlant[];
    isActive: boolean;
    generatesTip: boolean;
    capitalGrant: boolean;
}

export interface NamedECEconomicsAdvancedUserPlant {
    name: string;
    isActive: boolean;
    generatesTip: boolean;
}

export function arePlantsActive(pod: NamedECEconomicsThirdPartyProducerPod | NamedECEconomicsAdvancedUserPod) {
    return pod.namedPlants.map(plant => plant.isActive).reduce((acc, val) => acc && val, true);
}

export function showDedicatedShare(pod: NamedECEconomicsThirdPartyProducerPod | NamedECEconomicsAdvancedUserPod) {
    return arePlantsActive(pod) && isDefined(pod.net_tip) && isDefined(pod.tip_configs.plant_tip) && isDefined(pod.plant_tip);
}

export function showCacv(pod: NamedECEconomicsThirdPartyProducerPod | NamedECEconomicsAdvancedUserPod) {
    return isDefined(pod.plant_c_acv);
}

export function getTip(pod: NamedECEconomicsThirdPartyProducerPod | NamedECEconomicsAdvancedUserPod, nvp: NiceValuePipe, i18n: I18NService) {
    const tipMode = pod.tip_configs.mode;
    const tipType = pod.tip_configs.type;
    let plantTip: string;
    let tipText: string;
    let tip: string;
    if (tipMode === ECPlantTipMode.PERCENTAGE) {
        plantTip = nvp.transform(pod.tip_configs.plant_tip * 100, '%');
        switch (tipType) {
            case ECPlantTipType.NET:
                tipText = i18n.translate('Net TIP');
                tip = nvp.transform(pod.net_tip, '€');
                break;
            case ECPlantTipType.GROSS:
                tipText = i18n.translate('Gross TIP');
                tip = nvp.transform(pod.gross_tip, '€');
                break;
            case ECPlantTipType.NON_SURPLUS:
                tipText = i18n.translate('Non Surplus TIP');
                tip = nvp.transform(pod.non_surplus_tip, '€');
                break;
        }
    } else if (tipMode === ECPlantTipMode.EURO_MGW) {
        plantTip = nvp.transform(pod.tip_configs.plant_tip, '€/MWh');
        switch (tipType) {
            case ECPlantTipType.NET:
            case ECPlantTipType.GROSS:
                tipText = i18n.translate('Incentive shared energy');
                tip = nvp.transform(pod.gse ? pod.incentive_shared_energy / 1000 : pod.estimated_incentive_shared_energy / 1000, 'MWh', 5);
                break;
            case ECPlantTipType.NON_SURPLUS:
                tipText = i18n.translate('Non Surplus TIP');
                tip = nvp.transform(pod.incentivized_shared_energy_non_surplus / 1000, 'MWh', 5);
                break;
        }
    }
    return `(${tipText} ${tip} * ${plantTip})`;
}

export function getCacv(pod: NamedECEconomicsThirdPartyProducerPod | NamedECEconomicsAdvancedUserPod, cacvMode: ECCacvRedistributionMode, i18n: I18NService) {
    if (cacvMode === ECCacvRedistributionMode.AS_TIP || cacvMode === ECCacvRedistributionMode.PLANTS) {
        switch (pod.tip_configs.type) {
            case ECPlantTipType.NET:
            case ECPlantTipType.NON_SURPLUS:
                return `(${i18n.translate('Net {0}', CACV_STRING)}`;
            case ECPlantTipType.GROSS:
                return `(${i18n.translate('Gross {0}', CACV_STRING)}`;
        }
    } else {
        return `(${CACV_STRING}`;
    }
}

export function getPlantCacv(pod: NamedECEconomicsThirdPartyProducerPod | NamedECEconomicsAdvancedUserPod, cacvMode: ECCacvRedistributionMode, nvp: NiceValuePipe): string {
    if (cacvMode === ECCacvRedistributionMode.PLANTS) {
        return `${formatEuroValue(pod.net_c_acv ?? 0, nvp)}) =`;
    } else if (cacvMode === ECCacvRedistributionMode.REDISTRIBUTION) {
        return `${formatEuroValue(pod.plant_c_acv ?? 0, nvp)}) =`;
    } else if (cacvMode === ECCacvRedistributionMode.AS_TIP) {
        const tipMode = pod.tip_configs.mode;
        const tipType = pod.tip_configs.type;
        if (tipMode === ECPlantTipMode.PERCENTAGE) {
            const plantTip = formatPercentValue(pod.tip_configs.plant_tip ?? 0, nvp);
            let cacvValue = 0;
            if (tipType === ECPlantTipType.GROSS) {
                cacvValue = pod.gross_c_acv;
            } else if (tipType === ECPlantTipType.NET || tipType === ECPlantTipType.NON_SURPLUS) {
                cacvValue = pod.net_c_acv;
            }
            const cacv = formatEuroValue(cacvValue ?? 0, nvp);
            return `${cacv} * ${plantTip}) =`;
        } else if (tipMode === ECPlantTipMode.EURO_MGW) {
            const plantTip = formatEuroValue(pod.plant_tip ?? 0, nvp);
            let tipValue = 0;
            let cacvValue = 0;
            if (tipType === ECPlantTipType.GROSS) {
                tipValue = pod.gross_tip;
                cacvValue = pod.gross_c_acv;
            } else if (tipType === ECPlantTipType.NET) {
                tipValue = pod.net_tip;
                cacvValue = pod.net_c_acv;
            } else if (tipType === ECPlantTipType.NON_SURPLUS) {
                tipValue = pod.non_surplus_tip;
                cacvValue = pod.net_c_acv;
            }
            const tip = formatEuroValue(tipValue ?? 0, nvp);
            const cacv = formatEuroValue(cacvValue ?? 0, nvp);
            return `${cacv} * (${plantTip} / ${tip})) =`;
        }
    } else {
        return `${formatEuroValue(pod.plant_c_acv ?? 0, nvp)}) =`;
    }
}

export function getDedicatedShare(pod: NamedECEconomicsThirdPartyProducerPod | NamedECEconomicsAdvancedUserPod, nvp: NiceValuePipe) {
    return nvp.transform(isDefined(pod.plant_c_acv) ? pod.plant_c_acv + pod.plant_tip : pod.plant_tip, '€');
}

export function formatEuroValue(value: number, nvp: NiceValuePipe) {
    return nvp.transform(value, '€', 2, value < 0);
}

export function formatPercentValue(value: number, nvp: NiceValuePipe) {
    return nvp.transform(value * 100, '%');
}

// see ECMemberRelationType
export enum ECMemberPodRole {
    OWNER = 'owner',
    HOLDER = 'holder',
}

export function validateDocumentType(ctrl: AbstractControl): ValidationErrors | null {
    const errs: ValidationErrors = {};
    const ctrls: { [ctrl: string]: AbstractControl } = (ctrl as FormGroup).controls;
    // custom type only mandatory for `OTHER` type documents
    if ((ctrls.type.value === EC_DOCUMENT_TYPE_OTHER)
        && !mkTextField(ctrls.customType.value)) {
        ctrls.customType.setErrors({[FormError.EMPTY]: true});
    } else {
        ctrls.customType.setErrors(null);
    }
    return Object.values(errs).every(err => !err) ? null : errs;
}

interface MimeType {
    name: string;
    type: string;
}

const FILE_MIME_TYPES: MimeType[] = [
    {
        type: 'application/pdf',
        name: 'PDF'
    },
    {
        type: 'image/jpeg',
        name: 'JPG'
    },
    {
        type: 'image/png',
        name: 'PNG'
    }
];

export const MAX_FILE_MB = 2;

export function mimeTypesNames(): string {
    return FILE_MIME_TYPES.map(mt => mt.name).join(', ');
}

export function mimeTypes(): string[] {
    return FILE_MIME_TYPES.map(mt => mt.type);
}

export function validateFile(file: File, msgBoxService: MsgBoxService, i18n: I18NService): boolean {
    if (file.size > MAX_FILE_MB * 1048576) {
        msgBoxService.error(i18n.translate('Size of file {0} must be below {1} MB', file.name, MAX_FILE_MB));
        return false;
    }
    if (!mimeTypes().includes(file.type)) {
        msgBoxService.error(i18n.translate('Accepts only {0}', mimeTypesNames()));
        return false;
    }
    return true;
}

export interface ECMemberIdRelation extends ECMemberRelation {
    memberId: string;
}

/**
 * Sort all the given relations by start date in descending order, treating `null` as "more recent"
 */
export function sortMemberRelations(relations: ECMemberRelation[]): ECMemberRelation[] {
    return _.sortBy(relations,
        relation => isDefined(relation.start_date) ?
            -relation.start_date :
            Number.MIN_SAFE_INTEGER);
}

/**
 * Returns holders of the given POD, assuming all relations are related to it.
 * Results are sorted in descending order by relation "time span".
 * `podRelations` are expected to be of target type `ECMemberRelationTargetType.POD`
 */
export function getPodHolders(podRelations: { [memberId: string]: ECMemberRelation[] }): ECMemberIdRelation[] {
    const allRelations = _.flatMap(_.entries(podRelations),
        ([memberId, relations]) => relations.map(relation => [memberId, relation] as [string, ECMemberRelation]));

    const allRelationsByType = (type: ECMemberRelationType) => {
        const filteredRelations = allRelations.filter(([ignore, relation]) => relation.type === type)
            .map(([memberId, relation]) => ({...relation, memberId} as ECMemberIdRelation));
        return _.sortBy(filteredRelations, relation => isDefined(relation.start_date) ?
            -relation.start_date :
            Number.MIN_SAFE_INTEGER);
    };

    return allRelationsByType(ECMemberRelationType.HOLDER);
}

/**
 * Returns the *current* holder/owner of the given POD, assuming all relations are related to it.
 * Relations with `null` dates are treated as "more recent".
 * `podRelations` are already filtered by `ECMemberRelationTargetType.POD`
 */
export function getPodCurrentHolderAndOwner(podRelations: { [memberId: string]: ECMemberRelation[] }): {
    holder?: ECMemberIdRelation,
    owner?: ECMemberIdRelation
} {
    const allRelations = _.flatMap(_.entries(podRelations),
        ([memberId, relations]) => relations.map(relation => [memberId, relation] as [string, ECMemberRelation]));

    const allRelationsByType = (type: ECMemberRelationType) => {
        const filteredRelations = allRelations.filter(([ignore, relation]) => relation.type === type);
        const currentRelation = _.maxBy(filteredRelations, ([ignore, relation]) => isDefined(relation.start_date) ?
            relation.start_date :
            Number.MAX_SAFE_INTEGER);
        return currentRelation ? {...currentRelation[1], memberId: currentRelation[0]} : undefined;
    };

    return {
        holder: allRelationsByType(ECMemberRelationType.HOLDER),
        owner: allRelationsByType(ECMemberRelationType.OWNER)
    };
}
